import type { ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import type { QuerySchemaRead, QuerySchemaWalker } from './query-schema';
import { RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT } from '#layers/data-model/constants/related-values';
import { mapQueryCapability, QueryCapabilityKinds, resolveCollectionQueryCapability } from './query-capability';

/** What a relation include reads of its related rows. */
export const RelationRowReads = {
	/** Every related row: an include without a `limit`, as a form editing the relation sends. Never a preview. */
	Every: 'every',
	/** The one related row of a to-one relation. */
	Single: 'single',
	/** At most `limit` related rows: a to-many preview of a relation whose read accepts `limit`. */
	Capped: 'capped',
	/**
	 * No rows: a to-many preview of a relation whose read accepts no `limit`. The preview would read
	 * every related row, so it is left out and shown as not loaded.
	 */
	Unavailable: 'unavailable',
} as const;

type Reads = typeof RelationRowReads;

interface CappedRead {
	readonly kind: Reads['Capped'];
	readonly limit: number;
}

/** How lists, cards, and display templates preview a relation field. */
export type RelationPreview = { readonly kind: Reads['Single'] | Reads['Unavailable'] } | CappedRead;

/** What a relation include sent to the engine reads. */
export type RelationIncludeRead = { readonly kind: Reads['Every'] | Reads['Unavailable'] } | CappedRead;

/** One row past what the related-values dropdown lists, so it can tell that there are more. */
const PREVIEW_ROW_LIMIT = RELATED_VALUES_DROPDOWN_MAX_ITEM_COUNT + 1;

/** The one rule for a to-many preview: capped where the relation's read accepts `limit`. */
function toManyPreview(read: QuerySchemaRead, limit: number): CappedRead | { readonly kind: Reads['Unavailable'] } {
	return read.limit() ? { kind: RelationRowReads.Capped, limit } : { kind: RelationRowReads.Unavailable };
}

/**
 * How a relation field is previewed under the workspace query schema.
 *
 * Relations on system collections and Studio-only relations are ungated and capped. A schema that is
 * not loaded, or that does not return the relation, fails closed like any other query capability.
 */
export function resolveRelationPreview(
	field: Pick<ResolvedMonospaceSingleRelationField, 'id' | 'isList' | 'isStudioOnly' | 'collection'>,
	schema: QuerySchemaWalker | null | undefined,
): RelationPreview {
	if (!field.isList) return { kind: RelationRowReads.Single };
	if (field.isStudioOnly) return { kind: RelationRowReads.Capped, limit: PREVIEW_ROW_LIMIT };

	const read = mapQueryCapability(
		resolveCollectionQueryCapability(field.collection, schema),
		(api) => api.readMany()?.output()?.relationReadById(field.id),
	);

	switch (read.kind) {
		case QueryCapabilityKinds.Ungated:
			return { kind: RelationRowReads.Capped, limit: PREVIEW_ROW_LIMIT };
		case QueryCapabilityKinds.Unavailable:
		case QueryCapabilityKinds.Unsupported:
			return { kind: RelationRowReads.Unavailable };
		case QueryCapabilityKinds.Supported:
			return toManyPreview(read.value, PREVIEW_ROW_LIMIT);
		default:
			return read satisfies never;
	}
}

/**
 * What an include of a relation whose read is `read` fetches: a preview when it asks for at most
 * `limit` rows, otherwise every related row.
 */
export function resolveRelationIncludeRead(limit: number | undefined, read: QuerySchemaRead): RelationIncludeRead {
	if (limit === undefined) return { kind: RelationRowReads.Every };
	return toManyPreview(read, limit);
}
